'use strict';

/**
 * Sample-set details: filter facts to the selected sample set's current
 * correlation, bind a detail graph, and map nodes onto pizza-tracker stages
 * for highlighting.
 */

import { buildPipelineStatusGraphViewModel } from './pipelineStatusAdapter';
import {
	CORE_PROGRESS_STAGE_IDS,
	CORE_PROGRESS_SUB_STEP_LABELS,
	DISCOVERY_PROGRESS_SUB_STEP_LABELS,
	KITS_PROGRESS_SUB_STEP_LABELS,
} from './pipelineProgressCopy';

/** @param {string | null | undefined} correlationId */
function isEmptyPipelineCorrelation(correlationId) {
	const c = correlationId == null ? '' : String(correlationId).trim();
	return c === '' || c === '00000000-0000-0000-0000-000000000000';
}

/**
 * @param {import('./pipelineStatusTypes').PipelineStatusFact[]} facts
 * @param {{ sampleSetId: string, correlationId?: string | null }} selection
 */
export function filterCoreFactsForSampleSetFlow(facts, selection) {
	const sampleSetId = String(selection?.sampleSetId ?? '').trim();
	const correlationId = selection?.correlationId != null ? String(selection.correlationId).trim() : '';
	const rows = Array.isArray(facts) ? facts : [];
	return rows.filter((f) => {
		if (!f) return false;
		if (String(f.iterationValue ?? '').trim() !== sampleSetId) return false;
		if (!correlationId || isEmptyPipelineCorrelation(correlationId)) return true;
		return String(f.correlationId ?? '').trim() === correlationId;
	});
}

const KITS_INGEST_STAGES = new Set(['ManifestAvailable', 'ProjectAvailable']);

/**
 * Kits publication facts (current sample-set correlation) plus project-level ingest
 * hops (ManifestAvailable / ProjectAvailable) that do not share DataNorm's correlation.
 * @param {import('./pipelineStatusTypes').PipelineStatusFact[]} facts
 * @param {{ sampleSetId: string, correlationId?: string | null }} selection
 * @param {string} [projectId]
 */
export function filterKitsFactsForSampleSetFlow(facts, selection, projectId) {
	const project = projectId != null ? String(projectId).trim() : '';
	const belongsToProject = (fact) => !project || String(fact?.subjectId ?? '').trim() === project;
	const publication = filterCoreFactsForSampleSetFlow(facts, selection).filter(belongsToProject);
	const seen = new Set(publication.map((f) => f?.id).filter(Boolean));
	const ingest = (Array.isArray(facts) ? facts : []).filter((f) => {
		if (!f || seen.has(f.id)) return false;
		if (String(f.pipeline ?? '').trim().toLowerCase() !== 'kits') return false;
		if (!KITS_INGEST_STAGES.has(String(f.stage ?? '').trim())) return false;
		if (!belongsToProject(f)) return false;
		return true;
	});
	return [...publication, ...ingest];
}

/** @param {import('./pipelineStatusTypes').PipelineStatusStage} node */
function subStepStateFromNode(node) {
	switch (node.status) {
		case 'failed':
			return 'failed';
		case 'success':
		case 'skipped':
			return 'complete';
		case 'running':
			return 'active';
		default:
			return 'pending';
	}
}

/**
 * @param {{
 *   sampleSet: import('./pipelineCoreProgressModel').CoreSampleSetProgress,
 *   facts: import('./pipelineStatusTypes').PipelineStatusFact[],
 *   definition: import('./pipelineStatusTypes').PipelineStatusGraphDefinition,
 *   subStepLabels: Record<string, string>,
 *   filterFacts?: (facts: import('./pipelineStatusTypes').PipelineStatusFact[], selection: { sampleSetId: string, correlationId?: string | null }, projectId?: string) => import('./pipelineStatusTypes').PipelineStatusFact[],
 *   projectId?: string,
 * }} args
 */
export function buildSampleSetDetailBundle({
	sampleSet,
	facts,
	definition,
	subStepLabels,
	filterFacts = filterCoreFactsForSampleSetFlow,
	projectId,
}) {
	const filtered = filterFacts(
		facts,
		{
			sampleSetId: sampleSet.sampleSetId,
			correlationId: sampleSet.correlationId,
		},
		projectId,
	);
	const stages = buildPipelineStatusGraphViewModel(definition, filtered, [], projectId);

	const byHighLevel = new Map();
	for (const node of stages) {
		const hl = node.highLevelStage != null ? String(node.highLevelStage).trim() : '';
		if (!hl) continue;
		const list = byHighLevel.get(hl) ?? [];
		list.push(node);
		byHighLevel.set(hl, list);
	}

	const steps = (sampleSet.steps ?? []).map((step) => {
		const children = byHighLevel.get(step.id) ?? [];
		const subSteps = children.map((n) => ({
			id: n.id,
			label: subStepLabels[n.id] || n.description || n.id,
			state: subStepStateFromNode(n),
			occurredAt: n.occurredAt,
			count: undefined,
		}));
		return {
			id: step.id,
			label: step.label,
			state: step.state,
			count: undefined,
			subSteps,
		};
	});

	const currentStepId =
		steps.find((s) => s.state === 'active' || s.state === 'failed' || s.state === 'delayed')?.id
		?? (sampleSet.state === 'ready' ? CORE_PROGRESS_STAGE_IDS.ready : steps[0]?.id);

	return { steps, stages, currentStepId };
}

export function buildCoreSampleSetDetailBundle(sampleSet, facts, options) {
	const definition = options?.definition;
	if (!definition) {
		throw new Error('[pipelineStatus] Core sample-set details require a PortalApi graph definition.');
	}
	return buildSampleSetDetailBundle({
		sampleSet,
		facts,
		definition,
		subStepLabels: CORE_PROGRESS_SUB_STEP_LABELS,
		projectId: options?.projectId,
	});
}

export function buildDiscoverySampleSetDetailBundle(sampleSet, facts, options) {
	const definition = options?.definition;
	if (!definition) {
		throw new Error('[pipelineStatus] Discovery sample-set details require a PortalApi graph definition.');
	}
	return buildSampleSetDetailBundle({
		sampleSet,
		facts,
		definition,
		subStepLabels: DISCOVERY_PROGRESS_SUB_STEP_LABELS,
		projectId: options?.projectId,
	});
}

export function buildKitsSampleSetDetailBundle(sampleSet, facts, options) {
	const definition = options?.definition;
	if (!definition) {
		throw new Error('[pipelineStatus] Kits sample-set details require a PortalApi graph definition.');
	}
	return buildSampleSetDetailBundle({
		sampleSet,
		facts,
		definition,
		subStepLabels: KITS_PROGRESS_SUB_STEP_LABELS,
		filterFacts: filterKitsFactsForSampleSetFlow,
		projectId: options?.projectId,
	});
}
